import { useCurrentUser } from '@/hooks/useCurrentUser';
import ParentHeader from '@/components/common/ParentHeader';
import { Calendar, User, FileText, AlertCircle, Pencil, ChevronRight, UtensilsCrossed, Bike, Clock, CalendarDays, School } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import UpdateProfileModal from '@/components/modals/UpdateProfileModal';
import AddChildModal from '@/components/modals/AddChildModal';
import { getChildren, getParentProfile, getParentLeaves, sendLunchReadyUpdate, getLunchStatus, getMyLunchUpdates, getChildrenForLunchUpdate, updateChild, getDiscountCheckForChildService } from '@/services/parent';
import { fontSize, fontWeight, textColor } from '@/theme';
import { useScreenPermission } from '@/hooks/useScreenPermission';
import useBuyNow from "@/hooks/useBuyNow";
import PaymentModal from '@/components/modals/PaymentModal';
import PaymentProcessingLoader from '@/components/modals/PaymentProgressingModal';
import FreeTrialModal from '@/components/modals/FreeTrailModal';
import { fetchMatchingDiscountChildren } from '@/lib/utils/discount';


type LunchChild = {
  Child_Name: string;
  Line_item_Id: string;
};

export default function ParentDashboardComponent() {
  const userDetails = useCurrentUser();
  const username = userDetails?.username || 'User';
  const navigate = useNavigate();
  const trail_status = userDetails?.trail_status
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false);
  const [editingChild, setEditingChild] = useState<any>(null);
  const [children, setChildren] = useState<any[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [startSubscription, setStartSubscription] = useState(false);

  // ── Lunch Ready ────────────────────────────────────────────────────────────
  const [lunchSent, setLunchSent] = useState(false);
  const [lunchStatus, setLunchStatus] = useState<string | null>(null); // kept for API state tracking
  const [pickupDone, setPickupDone] = useState(false);
  const [deliveryDone, setDeliveryDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [trackSent, setTrackSent] = useState(false);
  const [trackSending, setTrackSending] = useState(false);
  const [isLunchWindow, setIsLunchWindow] = useState(() => {
    const h = new Date().getHours();
    return h >= 11 && h < 13;
  });
  const [isTrackWindow, setIsTrackWindow] = useState(() => {
    const h = new Date().getHours();
    return h >= 12 && h < 13;
  });
  const [childrenForLunchReady, setChildrenForLunchReady] = useState<LunchChild[]>([]);
  const { canCreate: canCreateChild, canUpdate: canUpdateChild } = useScreenPermission("child");
  const [parentProfile, setParentProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [showLunchModal, setShowLunchModal] = useState(false);
  const [selectedChildren, setSelectedChildren] = useState<string[]>([]);
  const [renewalChild, setRenewalChild] = useState<boolean>(false);
  const [showTrialModal, setShowTrialModal] = useState(false);

  const {
    openBuyNowModal,
    paymentData,
    openBuyNow,
    closeBuyNow,
    handleBuyNow,
    isProcessingPayment,
    rewardBalance,
    offers,
    specialOffers,
    selectedOfferAmount,
    setSelectedOfferAmount,
    usedRewardAmount,
    setUsedRewardAmount,
    paymentCalculation,
    showSavingsModal,
    selectedSpecialOfferAmount,
    setSelectedSpecialOfferAmount,
  } = useBuyNow({
    username,
    onSuccess: () => {
      loadChildren();
    },
    onRefresh: async () => {
      loadChildren();
    }
  });

  useEffect(() => {
    const id = setInterval(() => {
      const h = new Date().getHours();
      setIsLunchWindow(h >= 11 && h < 13);
      setIsTrackWindow(h >= 12 && h < 13);
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (trail_status === false || trail_status === "false") {
      setShowTrialModal(true);
    } else {
      setShowTrialModal(false);
    }
  }, [trail_status]);

  useEffect(() => {
    const hasOpenModal =
      showUpdateProfile ||
      showAddChild ||
      showLunchModal ||
      openBuyNowModal ||
      showTrialModal ||
      isProcessingPayment;

    if (hasOpenModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [
    showUpdateProfile,
    showAddChild,
    showLunchModal,
    openBuyNowModal,
    showTrialModal,
    isProcessingPayment,
  ]);

  useEffect(() => {
    if (!username || username === 'User') return;
    const today = new Date().toISOString().split('T')[0];
    getLunchStatus(username).then((res) => {
      if (res.status === 200 && res.data) {
        const data = res.data as any;
        const list: any[] = Array.isArray(data) ? data : data?.results ?? [];
        const todayEntry = list.find((item: any) => item.Date === today);
        if (todayEntry) {
          setLunchStatus(todayEntry.Status);
          setLunchSent(true);
          if (todayEntry.Status === 'Track_Delivery') setTrackSent(true);
          // fetch live pickup/delivery state
          getMyLunchUpdates().then((tr) => {
            const record = (tr.data as any)?.data?.[0] ?? tr.data;
            setPickupDone(Array.isArray(record?.Pickup_Data) && record.Pickup_Data.length > 0);
            setDeliveryDone(Array.isArray(record?.Delivery_Update) && record.Delivery_Update.length > 0);
          }).catch(() => { });
        }
      }
    }).catch(() => { });
  }, [username]);

  useEffect(() => {
    loadLunchChildren();
  }, []);

  const handleSelectChild = (id: string) => {
    setSelectedChildren((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const handleTrackDelivery = async () => {
    if (trackSent || trackSending) return;
    navigate('/parent-lunch-updates')
  };

  const handleLunchReady = async () => {
    if (!selectedChildren.length) {
      toast.error("Please select at least one child");
      return;
    }

    setSending(true);

    try {
      const payload = {
        Child: selectedChildren,
        Status: "Ready",
      };

      const response = await sendLunchReadyUpdate(payload);

      if (response.status === 200 || response.status === 201) {
        toast.success("Lunch status updated!");
        setLunchSent(true);
        setSelectedChildren([]);
        setShowLunchModal(false);
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSending(false);
    }
  };

  const loadLunchChildren = async () => {
    try {
      const response = await getChildrenForLunchUpdate();

      const data = response.data as LunchChild[];

      setChildrenForLunchReady(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log(error);
    }
  };

  const loadProfile = useCallback(async () => {
    if (!username || username === 'User') return;
    setProfileLoading(true);
    try {
      const response = await getParentProfile(username);
      if (response.status === 200 && response.data) {
        const data = Array.isArray(response.data) ? (response.data as any[])[0] : response.data;
        setParentProfile(data);
        const incomplete = !data?.Latitude || !data?.Longitude || !data?.State || !data?.City;
        setProfileIncomplete(incomplete);
        if (incomplete) setShowUpdateProfile(true);
      }
    } catch (error) {
      // console.error('Failed to load profile:', error);
    } finally {
      setProfileLoading(false);
    }
  }, [username]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const loadChildren = useCallback(async () => {
    if (!username || username === 'User') return;
    setLoadingChildren(true);
    try {
      const response = await getChildren(username);
      if (response.status === 200 && response.data) {
        const data = response.data as any;
        const userData = Array.isArray(data) ? data[0] : data;
        const childList = userData?.childs || userData?.Childs || [];
        setChildren(Array.isArray(childList) ? childList : []);
      }
    } catch (error) {
      // console.error('Failed to load children:', error);
    } finally {
      setLoadingChildren(false);
    }
  }, [username]);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const loadLeaves = useCallback(async () => {
    if (!username || username === 'User') return;
    setLoadingLeaves(true);
    try {
      const response = await getParentLeaves(username);
      if (response.status === 200 && response.data) {
        const data = response.data as any;
        const list = Array.isArray(data) ? data : data?.results ?? [];
        setLeaves(list);
      }
    } catch (error) {
      // console.error('Failed to load leaves:', error);
    } finally {
      setLoadingLeaves(false);
    }
  }, [username]);

  useEffect(() => {
    loadLeaves();
  }, [loadLeaves]);

  const handleEditChild = async (child: any) => {
    try {
      const response = await getChildren(username);

      let latestChild = child;

      if (response.status === 200 && response.data) {
        const data = response.data as any;
        const userData = Array.isArray(data)
          ? data[0]
          : data;

        const childList =
          userData?.childs ||
          userData?.Childs ||
          [];

        if (Array.isArray(childList)) {
          latestChild =
            childList.find(
              (c: any) => c.id === child.id
            ) || child;
        }

        setChildren(
          Array.isArray(childList)
            ? childList
            : []
        );
      }

      setEditingChild(latestChild);
      setShowAddChild(true);
    } catch (error) {
      console.error(
        "Failed to load child before editing:",
        error
      );

      setEditingChild(child);
      setShowAddChild(true);
    }
  };

  const handleStartTrial = async (childIds: number[]) => {
    try {
      const payload = {
        Trail: true,
        Record:'Trail'
      }
      for (const childId of childIds) {
        await updateChild(childId, payload)
      }

      toast.success("Free Trial Activated");
      closeBuyNow();
    } catch (error) {
      toast.error("Unable to activate free trial");
    }
  };

  const handleCloseChildModal = async () => {
    setShowAddChild(false);
    setEditingChild(null);
    setRenewalChild(false);
    setStartSubscription(false);
    loadChildren();
  };

  const handleChildSuccess = async () => {
    setShowAddChild(false);
    setEditingChild(null);
    setRenewalChild(false);
    setStartSubscription(false);
    loadChildren();
  };

  const isAllSelected =
    childrenForLunchReady.length > 0 &&
    selectedChildren.length ===
    childrenForLunchReady.length;

  const getFinalAmount = (child: any) => {
    const amount =
      child?.Range?.Final_Amount ??
      child?.Final_Amount ??
      child?.Amount;

    if (
      amount === undefined ||
      amount === null ||
      amount === ""
    ) {
      return null;
    }

    const parsed = parseFloat(String(amount));

    return Number.isFinite(parsed)
      ? parsed
      : null;
  };

  const handleSelectAllChildren = () => {
    if (isAllSelected) {
      setSelectedChildren([]);
    } else {
      setSelectedChildren(
        childrenForLunchReady.map(
          (child) => child.Line_item_Id
        )
      );
    }
  };

  const handleProfileSuccess = async () => {
    setShowUpdateProfile(false);
    await loadProfile();
  };

  const handleStartSubscription = (child: any) => {
    setStartSubscription(true);
    setRenewalChild(false);
    handleEditChild(child);
  };

  const handlePayChild = async (child: any) => {
    try {
      const response = await getChildren(username);

      let latestChild = child;

      if (response.status === 200 && response.data) {
        const data = response.data as any;

        const userData = Array.isArray(data)
          ? data[0]
          : data;

        const childList =
          userData?.childs ||
          userData?.Childs ||
          [];

        if (Array.isArray(childList)) {
          latestChild =
            childList.find(
              (c: any) =>
                String(c.id) === String(child.id)
            ) || child;

          setChildren(childList);
        }
      }

      // console.log("LATEST CHILD:", latestChild);

      const planName =
        latestChild?.Plan_Name || "";
      const range =
        latestChild?.Range?.Period ||
        latestChild?.Range ||
        "";

      const price = latestChild?.Range?.Base_Price

      if (!planName || !range) {
        toast.error("Plan or range is missing");
        return;
      }

      const existingDiscount = Number(
        latestChild?.child_discount || 0
      );

      const existingDiscountAmount = Number(
        latestChild?.child_discount_amount || 0
      );

      const savedFinalAmount = Number(
        latestChild?.Range?.Final_Amount ??
        latestChild?.Final_Amount ??
        latestChild?.Amount ??
        0
      );

      // console.log("CHILD DISCOUNT FROM CHILD API:", {
      //   existingDiscount,
      //   existingDiscountAmount,
      //   savedFinalAmount,
      // });

      let fullAmount = savedFinalAmount;

      if (
        existingDiscount > 0 &&
        existingDiscountAmount > 0
      ) {
        fullAmount = Number(
          (
            savedFinalAmount +
            existingDiscountAmount
          ).toFixed(2)
        );
      }

      if (
        !Number.isFinite(fullAmount) ||
        fullAmount <= 0
      ) {
        toast.error(
          "Unable to calculate subscription amount"
        );
        return;
      }


      const matchingChildren =
        await fetchMatchingDiscountChildren({
          username,
          planName,
          range,
          type: "Subscribed",
          currentChild: latestChild,
          price
        });

      console.log(
        "MATCHING DISCOUNT CHILDREN:",
        matchingChildren
      );

      const hasMatchingChild =
        matchingChildren.length > 0;

      const childDiscount =
        hasMatchingChild ? 50 : 0;

      const childDiscountAmount =
        hasMatchingChild
          ? Number(
            (fullAmount * 0.5).toFixed(2)
          )
          : 0;

      const childPayableAmount =
        Number(
          (
            fullAmount -
            childDiscountAmount
          ).toFixed(2)
        );

      console.log(
        "FINAL DISCOUNT CALCULATION:",
        {
          fullAmount,
          hasMatchingChild,
          childDiscount,
          childDiscountAmount,
          childPayableAmount,
        }
      );


      openBuyNow({
        subscriptionCode:
          latestChild?.Subscription_Code || "",

        childIds: [
          latestChild.id,
        ],

        totalAmount:
          childPayableAmount,
        Range: latestChild.Range,
        items: [
          {
            planName:
              latestChild?.Plan_Name || "",

            amount:
              childPayableAmount,

            duration:
              latestChild?.Range?.Period || "",

            range:
              latestChild?.Lal_Lon_Km ??
              latestChild?.DistanceKm ??
              0,
          },
        ],

        child: {
          ...latestChild,

          child_original_amount:
            fullAmount,

          child_discount:
            childDiscount,

          child_discount_amount:
            childDiscountAmount,

          Range: {
            ...latestChild?.Range,

            Final_Amount:
              childPayableAmount,
          },
        },
      });

    } catch (error: any) {
      console.error(
        "PAY CHILD ERROR:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "Unable to prepare payment"
      );
    }
  };


  const handleProfileClose = () => {
    if (!profileIncomplete) setShowUpdateProfile(false);
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4" />
          <p className={`${textColor.muted} ${fontWeight.medium}`}>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ParentHeader />

      <main className="max-w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className={`${fontSize['4xl']} ${fontWeight.bold} ${textColor.heading}`}>
              Welcome back, {username.toUpperCase()}!
            </h1>
            <p className={`${textColor.muted} mt-2`}>Manage your subscriptions and children</p>
          </div>
          <button
            onClick={() => setShowUpdateProfile(true)}
            className="flex items-center gap-2 px-6 py-3 bg-brand-500 text-white rounded-lg hover:bg-brand-700 transition-colors shadow-md"
          >
            <FileText className="w-5 h-5" />
            Update Profile
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`${fontSize.sm} ${textColor.muted} mb-1`}>Registered Children</p>
                <p className={`${fontSize['3xl']} ${fontWeight.bold} ${textColor.heading}`}>{children.length}</p>
              </div>
              <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center">
                <User className="w-7 h-7 text-green-600" />
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:border-yellow-300 transition-colors"
            onClick={() => navigate('/leaves')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`${fontSize.sm} ${textColor.muted} mb-1`}>Active Leaves</p>
                <p className={`${fontSize['3xl']} ${fontWeight.bold} ${textColor.heading}`}>
                  {leaves.filter(l => l.Leaves?.some((li: any) => li.Status?.toLowerCase() === 'confirm')).length}
                </p>
              </div>
              <div className="w-14 h-14 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FileText className="w-7 h-7 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`${fontSize.sm} ${textColor.muted} mb-1`}>Verification Status</p>
                <p className={`${fontSize['2xl']} ${fontWeight.bold} ${textColor.heading}`}>Pending</p>
              </div>
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Lunch Ready */}
        <div className={`relative rounded-2xl overflow-hidden shadow-sm border mb-8 transition-all duration-500 ${lunchSent ? 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50' : 'border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50'
          }`}>
          <div className={`absolute top-0 left-0 h-full w-1 ${lunchSent ? 'bg-green-400' : 'bg-orange-400'}`} />
          <div className="flex items-center justify-between px-8 py-5 gap-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${lunchSent ? 'bg-green-500' : isLunchWindow ? 'bg-orange-500' : 'bg-gray-200'
                }`}>
                {deliveryDone
                  ? <School className="w-6 h-6 text-white" />
                  : (pickupDone || lunchSent)
                    ? <Bike className="w-6 h-6 text-white" />
                    : <UtensilsCrossed className={`w-6 h-6 ${isLunchWindow ? 'text-white' : 'text-gray-400'}`} />
                }
              </div>
              <div>
                <p className={`${fontWeight.bold} text-base ${lunchSent ? 'text-green-800' : textColor.heading}`}>
                  {deliveryDone ? 'Delivered!' : pickupDone ? 'On the Way' : lunchSent ? 'Awaiting Pickup' : 'Lunch Ready?'}
                </p>
                <p className={`text-xs mt-0.5 ${lunchSent ? 'text-green-600' : textColor.muted}`}>
                  {deliveryDone
                    ? 'Lunch arrived safely at school.'
                    : pickupDone
                      ? 'Delivery partner is heading to school.'
                      : lunchSent
                        ? 'Waiting for delivery partner to arrive.'
                        : 'Tap to notify the delivery team once lunch is packed.'
                  }
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              {deliveryDone ? (
                <div className="px-5 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 bg-green-500 text-white">
                  <School className="w-4 h-4" />
                  Delivered
                </div>
              ) : (
                <button
                  onClick={() => {
                    if (lunchSent) {
                      handleTrackDelivery();
                    }
                    if (childrenForLunchReady.length === 0) {
                      toast.error("No children available");
                      return;
                    }
                    if (!lunchSent) {
                      setSelectedChildren([]);
                      setShowLunchModal(true);
                    }
                  }}
                  disabled={lunchSent ? false : (!isTrackWindow || trackSent || trackSending)}
                  className={`px-5 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all duration-300 ${lunchSent
                    ? 'bg-green-500 text-white cursor-pointer'
                    : isLunchWindow
                      ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-md hover:shadow-lg active:scale-[0.97]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                    }`}
                >
                  {sending || trackSending ? (
                    <>
                      <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      {sending ? 'Notifying…' : 'Updating…'}
                    </>
                  ) : pickupDone ? (
                    <>
                      <Bike className="w-4 h-4" />
                      In Transit
                    </>
                  ) : lunchSent ? (
                    <>
                      <Bike className="w-4 h-4" />
                      {trackSent ? 'Tracking Active' : 'Track Delivery'}
                    </>
                  ) : (
                    <>
                      <UtensilsCrossed className="w-4 h-4" />
                      Lunch Ready
                    </>
                  )}
                </button>
              )}

              {lunchSent && !deliveryDone && isTrackWindow && (
                <button
                  onClick={() => {
                    navigate('/parent-lunch-updates')
                  }}
                  disabled={trackSending}
                  className={`px-5 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all duration-300 ${trackSent
                    ? 'bg-green-500 text-white cursor-default'
                    : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg active:scale-[0.97]'
                    }`}
                >
                  {trackSending ? (
                    <>
                      <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      Updating…
                    </>
                  ) : (
                    <>
                      <Bike className="w-4 h-4" />
                      {trackSent ? 'Tracking Active' : 'Track Delivery'}
                    </>
                  )}
                </button>
              )}

              {!lunchSent && (
                <span className={`flex items-center gap-1 text-xs ${isLunchWindow ? 'text-orange-500' : 'text-gray-400'}`}>
                  <Clock className="w-3 h-3" />
                  {isLunchWindow ? 'Active now · 11 AM – 1 PM' : 'Available 11 AM – 1 PM'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Holidays quick link */}
        <button
          onClick={() => navigate('/parent-holidays')}
          className="w-full flex items-center justify-between bg-white rounded-xl shadow-sm border border-brand-100 px-6 py-4 mb-8 hover:shadow-md hover:border-brand-300 transition-all text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-5 h-5 text-brand-500" />
            </div>
            <div>
              <p className={`${fontWeight.semibold} ${textColor.heading} text-base`}>Holidays</p>
              <p className={`text-xs ${textColor.muted} mt-0.5`}>View public holidays &amp; festivals</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`${fontSize['2xl']} ${fontWeight.bold} ${textColor.heading}`}>
              Registered Children ({children.length})
            </h2>
            {canCreateChild && (
              <button
                onClick={() => { setEditingChild(null); setShowAddChild(true); }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                + Add Child
              </button>
            )}
          </div>

          {loadingChildren ? (
            <div className={`text-center py-12 ${textColor.subtle}`}>
              <p>Loading children…</p>
            </div>
          ) : children.length === 0 ? (
            <div className={`text-center py-12 ${textColor.subtle}`}>
              <p>No children registered yet. Add a child to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {children.map((child: any) => {
                return (
                  <div key={child.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex justify-between items-center">
                          <h3 className={`${fontWeight.bold} ${fontSize.lg} ${textColor.heading}`}>{child.Child_Name}</h3>
                          
                        </div>
                        <p className={`${textColor.muted} mt-1`}>
                          Age: {child.Age} | Class: {child.Class}
                          {child.Section ? ` | Section: ${child.Section}` : ''}
                          {child.Roll_No ? ` | Roll No: ${child.Roll_No}` : ''}
                        </p>
                        <p className={textColor.muted}>{child.School} School,</p>
                        {/* <p className={textColor.muted}>City: {child.City} | State: {child.State}</p> */}
                        <p className={textColor.muted}>{child.Address}</p>
                        {child.Plan_Name && (
                          <p className={textColor.muted}>
                            Plan: {child.Plan_Name}
                            {child.Range?.Period ? ` | ${child.Range.Period}` : ''}
                            {child.Range?.Final_Amount !== undefined &&
                              child.Range?.Final_Amount !== null
                              ? ` · ₹${parseFloat(String(child.Range.Final_Amount)).toFixed(2)}`
                              : ''}
                          </p>
                        )}
                        <div className='flex flex-row gap-4'>
                          <p className={textColor.muted}>{child.Subscription_Start_Date ? `From: ${child.Subscription_Start_Date}` : ''}</p>
                          <p className={textColor.muted}>{child.Subscription_End_Date ? `To: ${child.Subscription_End_Date}` : ''}</p>
                        </div>
                        <p className={`${fontSize.sm} mt-2`}>
                          {child.Record === "Trail" && (
                            child.Verification_Status === true ? (
                              <span className="text-green-600 font-medium whitespace-nowrap">
                                Verified
                              </span>
                            ) : (
                              <span className="text-orange-600 font-medium whitespace-nowrap">
                                Trail Verification Pending
                              </span>
                            )
                          )}

                          {(child.Record === "Subscribed" ||
                            child.Record === "Renewal") && (
                            child.Verification_Status === true ? (
                              <span className="text-green-600 font-medium whitespace-nowrap">
                                Verified
                              </span>
                            ) : (
                              <span className="text-orange-600 font-medium whitespace-nowrap">
                                Verification Pending
                              </span>
                            )
                          )}
                        </p>
                        </div>

                        <div className="flex flex-row gap-2 items-center">
                          {child.Record === "Trail" && (
                            <>
                              {child.Status === "Active" && (
                                <>
                                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600 font-semibold whitespace-nowrap">
                                    Trail Active
                                  </span>
                                </>
                              )}
                              {child.Status === "In-Active" && (
                                <>
                                  <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-600 font-semibold whitespace-nowrap">
                                    Trail Expired
                                  </span>

                                  <button
                                    onClick={() => handleStartSubscription(child)}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                                  >
                                    Start Plan
                                  </button>
                                </>
                              )}
                            </>
                          )}

                          {child.Record === "Subscribed" && (
                            <>

                              {child.Status === "In-Active" && (
                                <>
                                  <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-600 font-semibold whitespace-nowrap">
                                    Expired
                                  </span>

                                  <button
                                    onClick={() => {
                                      setRenewalChild(true);
                                      setStartSubscription(false);
                                      handleEditChild(child);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    Renewal
                                  </button>
                                </>
                              )}

                    
                              {child.Status === "Active" && (
                                <>
                                 
                                  {child.payment_status === "Pending" && (
                                    <button
                                      onClick={() => handlePayChild(child)}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                      Pay
                                    </button>
                                  )}

                                  {child.payment_status === "Paid" && (
                                    <>
                                    {child.Verification_Status === true && (
                                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600 font-semibold whitespace-nowrap">
                                        Active
                                      </span>)}
                                    <span className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg whitespace-nowrap">
                                      Paid
                                    </span>
                                    </>
                                  )}

                                  {child.payment_status === "Pending" &&
                                    child.Verification_Status === false &&
                                    canUpdateChild && (
                                      <button
                                        onClick={() => handleEditChild(child)}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-brand-500 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                        Edit
                                      </button>
                                    )}
                                </>
                              )}
                            </>
                          )}


                          {child.Record === "Renewal" && (
                            <>
                              
                              {child.Status === "In-Active" && (
                                <>
                                  <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-600 font-semibold whitespace-nowrap">
                                    Expired
                                  </span>

                                  <button
                                    onClick={() => {
                                      setRenewalChild(true);
                                      setStartSubscription(false);
                                      handleEditChild(child);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    Renewal
                                  </button>
                                </>
                              )}
                              {child.Status === "Active" && (
                                <>
                                 
                                  {child.payment_status === "Pending" && (
                                    <button
                                      onClick={() => handlePayChild(child)}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                      Pay
                                    </button>
                                  )}
                                  
                                  {child.payment_status === "Paid" && (
                                    <>
                                   {child.Verification_Status === true && (
                                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600 font-semibold whitespace-nowrap">
                                            Active
                                        </span>
                                    )}
                                    <span className="px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg whitespace-nowrap">
                                      Paid
                                    </span>
                                    </>
                                  )}

                                  {child.payment_status === "Pending" &&
                                    child.Verification_Status === false &&
                                    canUpdateChild && (
                                      <button
                                        onClick={() => handleEditChild(child)}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-brand-500 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                        Edit
                                      </button>
                                    )}
                                </>
                              )}
                            </>
                          )}

                        </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Leave Requests */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`${fontSize['2xl']} ${fontWeight.bold} ${textColor.heading}`}>
              Recent Leave Requests
            </h2>
            <button
              onClick={() => navigate('/leaves')}
              className="flex items-center gap-1 text-sm text-brand-500 hover:text-brand-700 font-semibold transition-colors"
            >
              View All
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {loadingLeaves ? (
            <div className={`text-center py-8 ${textColor.subtle} text-sm`}>Loading leaves…</div>
          ) : leaves.length === 0 ? (
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-5 flex items-center justify-between">
              <p className="text-brand-700 text-sm">No leave requests submitted yet.</p>
              <button
                onClick={() => navigate('/leaves')}
                className="text-sm font-semibold text-brand-500 hover:text-brand-700 flex items-center gap-1"
              >
                Apply Leave <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {leaves.slice(0, 3).map((group: any) => {
                const allCancelled = group.Leaves?.every((l: any) =>
                  ['cancle', 'cancelled'].includes(l.Status?.toLowerCase())
                );
                const date = new Date(group.Created_At).toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric',
                });
                return (
                  <div key={group.Leave_Id} className="flex items-center justify-between border border-gray-100 rounded-xl px-5 py-4 hover:bg-gray-50 transition-colors">
                    <div>
                      <p className={`${fontWeight.semibold} ${textColor.heading} text-sm`}>{group.Leave_Id}</p>
                      <p className={`text-xs ${textColor.muted} mt-0.5`}>
                        {group.Leaves?.length ?? 0} entries · {date}
                      </p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${allCancelled
                      ? 'bg-red-100 text-red-700 border-red-200'
                      : 'bg-green-100 text-green-700 border-green-200'
                      }`}>
                      {allCancelled ? 'Cancelled' : 'Active'}
                    </span>
                  </div>
                );
              })}
              {leaves.length > 3 && (
                <button
                  onClick={() => navigate('/leaves')}
                  className="w-full text-center text-sm text-brand-500 hover:text-brand-700 font-semibold py-2 transition-colors"
                >
                  View {leaves.length - 3} more requests →
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      {showLunchModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl p-6 w-[400px] max-h-[80vh] overflow-auto">

            <h2 className="text-xl font-bold mb-4">
              Select Children
            </h2>

            <div className="space-y-3">

              <label className="flex items-center justify-between border border-orange-200 bg-orange-50 p-3 rounded-lg cursor-pointer">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAllChildren}
                  />

                  <span className="font-semibold text-orange-700">
                    Select All Children
                  </span>
                </div>

                <span className="text-xs text-orange-500 font-medium">
                  {selectedChildren.length}/
                  {childrenForLunchReady.length} Selected
                </span>
              </label>

              {childrenForLunchReady.map((child) => (
                <label
                  key={child.Line_item_Id}
                  className={`flex items-center gap-3 border p-3 rounded-lg cursor-pointer transition-all ${selectedChildren.includes(
                    child.Line_item_Id
                  )
                    ? "border-orange-400 bg-orange-50"
                    : "border-gray-200"
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedChildren.includes(
                      child.Line_item_Id
                    )}
                    onChange={() =>
                      handleSelectChild(
                        child.Line_item_Id
                      )
                    }
                  />

                  <span className="font-medium">
                    {child.Child_Name}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setShowLunchModal(false)}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleLunchReady}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg"
              >
                Submit
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Modals */}
      <UpdateProfileModal
        isOpen={showUpdateProfile}
        onClose={handleProfileClose}
        onSuccess={handleProfileSuccess}
        profileData={parentProfile}
        isRequired={profileIncomplete}
      />
      <AddChildModal
        isOpen={showAddChild}
        onClose={handleCloseChildModal}
        onSuccess={handleChildSuccess}
        renewalChild={renewalChild}
        startSubscription={startSubscription}
        editingChild={editingChild}
      />
      <PaymentModal
        open={openBuyNowModal}
        onClose={closeBuyNow}
        data={paymentData}
        onBuyNow={handleBuyNow}
        rewardBalance={rewardBalance}
        offers={offers}
        specialOffers={specialOffers || []}
        selectedOfferAmount={selectedOfferAmount}
        setSelectedOfferAmount={setSelectedOfferAmount}
        usedRewardAmount={usedRewardAmount}
        setUsedRewardAmount={setUsedRewardAmount}
        paymentCalculation={paymentCalculation}
        showSavingsModal={showSavingsModal}
        setSelectedSpecialOfferAmount={setSelectedSpecialOfferAmount}
        selectedSpecialOfferAmount={selectedSpecialOfferAmount}
        onStartTrial={handleStartTrial}
      />
      <PaymentProcessingLoader visible={isProcessingPayment} />
      <FreeTrialModal
        open={showTrialModal}
        onClose={() => {
          setShowTrialModal(false)
        }}
        onStartTrial={() => {
          setShowTrialModal(false);
        }}
      />
    </div>
  );
}
